import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { finalize, Subject, takeUntil } from "rxjs";
import { AuthService } from "../../services/api/auth/auth.service";
import { DataService } from "../../services/api/data/data.service";
import { UserFieldService } from "../../services/user-field/user-field.service";
import { ThemeService } from "../../services/theme/theme.service";
import { DashboardMap } from "../components/dashboard-map/dashboard-map";
import { DashboardChart } from "../components/dashboard-chart/dashboard-chart";
import { DashboardTableComponent } from "../components/dashboard-table/dashboard-table.component";
import { ModalService } from "../../services/modal/modal.service";
import { LoadingService } from "../../services/loading/loading";
import { alertNoClaimedField } from "../utils/data-util";

@Component({
  selector: "app-grower-dashboard",
  standalone: true,
  imports: [DashboardMap, DashboardChart, DashboardTableComponent],
  templateUrl: "./grower-dashboard.component.html",
  styleUrl: "./grower-dashboard.component.scss",
  host: {
    class: "flex flex-col h-full overflow-hidden",
  },
})
export class GrowerDashboardComponent {
  claimedFields?: ClaimedField[];
  claimedFieldETs?: any;
  selectedclaimedField?: ClaimedField;
  claimedFieldsForMap?: any[];
  mode = localStorage.getItem("theme");
  private destroy$ = new Subject<void>();

  // Constructor
  constructor(
    private authService: AuthService,
    private dataService: DataService,
    private userFieldService: UserFieldService,
    private themeService: ThemeService,
    private modalService: ModalService,
    private loadingService: LoadingService,
    private router: Router,
  ) {}

  /**
   * Function of ngOnInit Lifecycle Hook
   */
  ngOnInit(): void {
    // Get Current Login User Id then fetch the claimedFields
    const userId = this.authService.getUserId();
    this.fetchClaimedFields(userId);

    // Subscribe to the selectedUserFieldId observable
    this.userFieldService.selectedUserFieldId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id: number | null) => {
        if (id) {
          if (!this.claimedFields?.length) {
            return;
          }
          // Switch the local variable selectedclaimedField to the new one
          this.selectedclaimedField = this.claimedFields?.find(
            (userfield) => userfield.id === id,
          );
          if (!this.selectedclaimedField) {
            // selectedUserFieldId$ is a BehaviorSubject, so dashboards can receive
            // a stale id from a previous screen before this grower's fields load.
            console.error("Selected claimed field not found");
            return;
          }
          this.claimedFieldETs = undefined;
        }
      });
    // Subscribe the mode (dark or light) and pass to Map (Mapbox)
    this.themeService.darkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDarkMode) => {
        this.mode = isDarkMode === true ? "dark" : "light";
      });
  }

  /**
   * Function for fetching claimed fields for a certain manager
   * @param userId
   */
  fetchClaimedFields(userId: string): void {
    // Subscribe the getClaimedFieldsManager, process & prepare the data
    this.loadingService.start({
      token: "grower-dashboard-claimed-fields",
      message: "Loading fields",
    });
    this.dataService
      .getClaimedFields(userId)
      .pipe(
        finalize(() =>
          this.loadingService.stop("grower-dashboard-claimed-fields"),
        ),
      )
      .subscribe({
        // Process the result (claimedFields)
        next: async (claimedFields: any[]) => {
          // Assign the claimedFields to the local variable
          this.claimedFields = claimedFields.map((field) => ({
            ...field,
            entities: Array.isArray(field.entities) ? field.entities : [],
          }));
          // If claimedFields is falsy value, pop up the content: no claimed user in the current manager's district.
          if (!this.claimedFields || this.claimedFields.length === 0) {
            await alertNoClaimedField(this.modalService);
            const mode =
              this.authService.internalModeValue === "demo" ? "demo" : null;
            this.router.navigate(["/field"], {
              queryParams: { mode },
              queryParamsHandling: "merge",
            });
            return;
          }
          // Extract properties id, field_id and geom from claimedFields for Map Using (Mapbox)
          this.claimedFieldsForMap = this.claimedFields.map(
            ({ id, field_id, entities, geom }) => {
              return { id, field_id, entities, geom };
            },
          );
        },
        // Error
        error: (error: any) => {
          console.error("Cannot Get Fields for the current Manager", error);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
